import { RAIL_TYPE_KEYS } from './railType';

/** How many modules of one family may be added. An address is 4 bits: 0–15. */
const MAX_MODULES_PER_FAMILY = 15;

/**
 * How many modules the RCU will carry in total, whatever their family. Rails and
 * DALI bridges come out of the same budget, so a device full of bridges has less
 * room for rails.
 */
const MAX_MODULES_TOTAL = 60;

/**
 * Hardware limits for one RCU. Every bound, range and cap in the app resolves
 * back to this object, so a change here is a change everywhere.
 */
export const RCU_LIMITS = {
  /** Onboard relay outputs an RCU can be configured with. */
  MIN_OUTPUTS: 1,
  MAX_OUTPUTS: 26,
  DEFAULT_OUTPUTS: 26,

  /** A rail's address is set by a 4-bit selector: 0–15 inclusive. */
  MIN_RAIL_ADDRESS: 0,
  MAX_RAIL_ADDRESS: 15,

  /** Rails of one type, and DALI bridges: the same per-family ceiling. */
  MAX_MODULES_PER_FAMILY,
  /** Deprecated alias kept for readability at rail call sites. */
  MAX_RAILS_PER_TYPE: MAX_MODULES_PER_FAMILY,
  MAX_DALI_BRIDGES: MAX_MODULES_PER_FAMILY,
  /** Rails plus DALI bridges, which share this budget. */
  MAX_MODULES_TOTAL,
} as const;

/** Rail families the module budget has to cover. */
export const RAIL_FAMILY_COUNT = RAIL_TYPE_KEYS.length;

/** DALI bridges are the one module family that is not a rail. */
export const DALI_BRIDGE_FAMILIES = 1;

/** Families competing for the budget, for the console's summary. */
export const MODULE_FAMILY_COUNT = RAIL_FAMILY_COUNT + DALI_BRIDGE_FAMILIES;

const createRange = (min: number, max: number): readonly number[] =>
  Array.from({ length: max - min + 1 }, (_unused, index) => min + index);

/** Selectable onboard output counts: 1…26. */
export const RCU_OUTPUT_RANGE: readonly number[] = createRange(
  RCU_LIMITS.MIN_OUTPUTS,
  RCU_LIMITS.MAX_OUTPUTS,
);

/** Selectable rail addresses: 0…15. */
export const RAIL_ADDRESS_RANGE: readonly number[] = createRange(
  RCU_LIMITS.MIN_RAIL_ADDRESS,
  RCU_LIMITS.MAX_RAIL_ADDRESS,
);
